import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { EventCard } from '../../../cards';
import Img from '../../../Img';
import LoadMoreButton from '../../../../pages/SearchPage/SearchV2/EventSearchResults/components/LoadMoreButton';
import { getApiAxiosInstance } from '../../../../utils/network';
import { showErrorPopup } from '../../../../actions/notification-actions';
import {
    PowerFunnelGatedFeatures,
    CppPlayerActions,
} from '../../../Analytics/constants';
import useCollegeProspectAccess from '../../../hooks/useCollegeProspectAccess';
import useCPPTracking from '../../../hooks/useCPPTracking';
import styles from '../collegeProspectTab.scss';

const TESTID_PREFIX = 'profile.college-prospect.recommended-events';
const RECOMMENDED_EVENTS_ENDPOINT = '/v1/cpp/recommended-events';
const RECOMMENDED_EVENTS_FETCH_LIMIT = 25;
const INITIAL_VISIBLE_EVENTS = 3;
const LOAD_MORE_INCREMENT = 5;
const MAX_LOCKED_PLACEHOLDERS = 3;
const LOCK_ICON_SRC = '/img/v2/icons/icon-feature-lock.svg';

const renderEventCard = (event, key, auth, onView) => (
    <div
        key={key}
        className={styles.recommendedEventCardWrapper}
        onClick={onView}
        onKeyDown={onView}
        role="presentation"
    >
        <EventCard
            event={event}
            auth={auth}
            force={{ cardView: true }}
            isProfilePage
        />
    </div>
);

const renderLockedPlaceholder = (key, testid, onUnlock) => (
    <div
        key={key}
        className={`${styles.recommendedEventCardWrapper} ${styles.recommendedEventCardLockedWrapper}`}
        role="button"
        tabIndex={0}
        onClick={onUnlock}
        onKeyDown={onUnlock}
        data-testid={testid}
    >
        <div className={`${styles.recommendedEventPlaceholder} ${styles.recommendedEventCardLocked}`} aria-hidden="true">
            <div className={styles.recommendedEventPlaceholderLine} />
            <div className={styles.recommendedEventPlaceholderLineShort} />
        </div>
        <span className={styles.recommendedEventLockOverlay}>
            <Img
                src={LOCK_ICON_SRC}
                alt=""
                className={styles.recommendedEventLockIcon}
            />
        </span>
    </div>
);

const RecommendedEvents = () => {
    const dispatch = useDispatch();
    const auth = useSelector((state) => state.auth);
    const { hasAnnualPower, openUpgradePopup } = useCollegeProspectAccess();
    const isLocked = !hasAnnualPower;
    const { trackPlayerEngagement } = useCPPTracking();

    const [events, setEvents] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_EVENTS);
    const [hasLoaded, setHasLoaded] = useState(false);

    const fetchEvents = useCallback(async () => {
        try {
            const response = await getApiAxiosInstance().get(RECOMMENDED_EVENTS_ENDPOINT, {
                params: { Top: RECOMMENDED_EVENTS_FETCH_LIMIT },
            });
            setEvents(response.data?.results || []);
            setTotalCount(response.data?.total || 0);
            setVisibleCount(INITIAL_VISIBLE_EVENTS);
        } catch (err) {
            dispatch(showErrorPopup({
                message: 'We couldn\'t load recommended events. Please try again.',
                error: err,
            }));
            setEvents([]);
            setTotalCount(0);
        } finally {
            setHasLoaded(true);
        }
    }, [dispatch]);

    // Refetch when annual Power is gained in-session (e.g. via the upgrade popup): the locked
    // fetch returns only the qualifying count, so the unlocked view needs the real event data.
    useEffect(() => {
        fetchEvents();
    }, [fetchEvents, hasAnnualPower]);

    const handleLoadMore = () => {
        setVisibleCount((count) => count + LOAD_MORE_INCREMENT);
    };

    const handleUnlock = () => {
        openUpgradePopup(PowerFunnelGatedFeatures.CPP_RECOMMENDED_EVENTS_LOCKED);
    };

    if (!hasLoaded) {
        return null;
    }

    const isEmpty = totalCount === 0;
    const visibleEvents = events.slice(0, visibleCount);
    const hasMoreEvents = visibleCount < events.length;
    const lockedPlaceholderCount = Math.min(totalCount, MAX_LOCKED_PLACEHOLDERS);

    return (
        <div className={styles.recommendedEvents} data-testid={`${TESTID_PREFIX}.container`}>
            <div className={styles.recommendedEventsTitle} data-testid={`${TESTID_PREFIX}.title`}>
                Recommended Events Near You
            </div>
            <p className={styles.recommendedEventsSubText} data-testid={`${TESTID_PREFIX}.subtext`}>
                These events include players within .5 of your rating giving you the best opportunity to improve your UTR.
            </p>
            {isEmpty && (
                <div className={styles.recommendedEventsEmpty} data-testid={`${TESTID_PREFIX}.empty`}>
                    There are no events near you that are open for registration with players at your level.
                </div>
            )}
            {!isEmpty && isLocked && (
                <div className={styles.recommendedEventsList}>
                    {Array.from({ length: lockedPlaceholderCount }, (unused, index) => renderLockedPlaceholder(
                        `recommended-locked-${index}`,
                        `${TESTID_PREFIX}.locked-placeholder-${index}`,
                        handleUnlock,
                    ))}
                </div>
            )}
            {!isEmpty && !isLocked && (
                <>
                    <div className={styles.recommendedEventsList}>
                        {visibleEvents.map((event, index) => renderEventCard(
                            event,
                            `recommended-event-${event.id ?? index}`,
                            auth,
                            () => trackPlayerEngagement(CppPlayerActions.CPP_RECOMMENDED_EVENT_CLICKED),
                        ))}
                    </div>
                    {hasMoreEvents && (
                        <div className={styles.recommendedEventsLoadMore}>
                            <LoadMoreButton
                                onLoadMore={handleLoadMore}
                                data-testid={`${TESTID_PREFIX}.load-more-btn`}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default RecommendedEvents;
